import React, { useEffect, useRef, useState } from 'react';
import { ReportAPI } from '../../../Api/ReportAPI';
import { ServiceInfo } from '../../../Utilis';
import VideoItem from './VideoItem';
import { ReactComponent as DownloadIcon } from "../../../Png/download-solid.svg";

const ReportItem = ({ report, setShowedReport, showReportId, canPreviewOriginalVideo = false }) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const [reportFilePdf, setReportFilePdf] = useState(null);
  const [videoReportProgress, setVideoReportProgress] = useState(0);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const isDownloading = useRef(false);
  const isFetchingVideo = useRef(false);

  const hasProcessedVideo = Boolean(report?.videoReportId);
  const hasOriginalVideo = Boolean(report?.videoId);
  const canShowAnyVideo = hasProcessedVideo || (canPreviewOriginalVideo && hasOriginalVideo);

  const displayVideoName = hasProcessedVideo
    ? (report.videoReport || report.videoName || 'video_report.mp4')
    : (report.videoName || 'uploaded_video.mp4');

  const handleViewDetails = async () => {
    setShowedReport();

    if (showVideo && videoUrl) return;
    if (isFetchingVideo.current) return;
    if (!canShowAnyVideo) return;

    isFetchingVideo.current = true;
    setIsPreviewLoading(true);

    try {
      const response = await ReportAPI.getPreviewVideoUrl(report.Id);
      const previewUrl = response?.data?.url;

      console.log("preview response:", response.data);
      console.log("previewUrl:", previewUrl);

      if (!previewUrl) {
        throw new Error("Preview URL missing");
      }

      setVideoUrl(previewUrl);
      setShowVideo(true);
    } catch (error) {
      console.error('Failed to load video preview:', error);
    } finally {
      isFetchingVideo.current = false;
      setIsPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (showReportId !== report.Id) {
      setShowVideo(false);
      setVideoUrl('');
    }
  }, [showReportId, report.Id]);

  const handleDownloadVideoReport = () => {
    if (isDownloading.current) return;

    isDownloading.current = true;

    ReportAPI.getVideoDownloadLink(report.Id, setVideoReportProgress)
      .then((response) => {
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.style.display = 'none';
        a.href = url;
        a.download = displayVideoName;

        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch((err) => console.error('Error downloading file:', err))
      .finally(() => {
        isDownloading.current = false;
        setVideoReportProgress(0);
      });
  };

  function fetchReport(fileType) {
    let filename = "";
    let report_data_id = null;

    switch (fileType) {
      case 0:
        if (report.pdfReport?.split(".").pop() !== "pdf") {
          fileType = 1;
        }
        report_data_id = report.pdfReportId;
        filename = report.pdfReport;
        break;
      case 1:
        filename = report.pdfReport;
        report_data_id = report.pdfReportId;
        break;
      case 2:
        filename = report.excelReport;
        report_data_id = report.excelReportId;
        break;
      default:
        return;
    }

    ReportAPI.getReportFile(report_data_id, fileType)
      .then((response) => {
        switch (fileType) {
          case 0:
            handlePdf(response.data);
            break;
          case 1:
            handleFileDownload(response.data, filename);
            break;
          case 2:
            handleFileDownload(response.data, filename);
            break;
          default:
            break;
        }
      })
      .catch((error) => {
        console.error("Error fetching report:", error);
      });
  }

  function handleFileDownload(blob, filename) {
    const fileUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');

    downloadLink.href = fileUrl;
    downloadLink.setAttribute('download', filename || 'download');
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    URL.revokeObjectURL(fileUrl);
  }

  function handlePdf(blob) {
    const file = new Blob([blob], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(file);
    setReportFilePdf(fileURL);
  }

  const parseDateTime = (datetime) => {
    const date = new Date(datetime);
    return `${date.getUTCHours()}:${date.getUTCMinutes()}h ${date.getUTCDate()}.${date.getUTCMonth() + 1}.${date.getUTCFullYear()}`;
  };

  return (
    <div className="report-item">
      <h3>{report.folderName}</h3>

      <p className='service'>
        Service: {
          Object.entries(ServiceInfo).filter(
            (service) => service[1].serviceNumber == report.serviceNumber
          )[0][1].displayName
        }
      </p>

      {report?.folder_uploaded && (
        <p className='service'>
          Uploaded time:
          <span className='date-info'>{parseDateTime(report.folder_uploaded)}</span>
          {report?.report_finished && (
            <span>
              {' '}Report finished:
              <span className='date-info'>{parseDateTime(report.report_finished)}</span>
            </span>
          )}
        </p>
      )}

      {(showVideo || isPreviewLoading) && showReportId == report["Id"] ? (
        <>
          <div className="pdfFileAndDownload">
            <h4>{hasProcessedVideo ? "Video report:" : "Original uploaded video:"}</h4>
            <a onClick={handleDownloadVideoReport}>
              <DownloadIcon className="icon" />
              {displayVideoName}
              {!hasProcessedVideo && canPreviewOriginalVideo ? " (original upload)" : ""}
            </a>

            {videoReportProgress > 0 && isDownloading.current ? (
              <div>
                <p>Download progress:</p>
                <progress value={videoReportProgress} max="100"></progress>
                <span>{videoReportProgress.toFixed(1)}%</span>
              </div>
            ) : null}
          </div>

          {isPreviewLoading ? (
            <p style={{ color: 'white' }}>Loading video preview...</p>
          ) : null}

          {canShowAnyVideo && showVideo ? (
            <VideoItem videoUrl={videoUrl} showVideo={showVideo} />
          ) : null}
        </>
      ) : null}

      {report.pdfReport && showReportId == report["Id"] ? (
        <div onClick={() => { fetchReport(0); }} className='pdfReport'>
          <h4>Report:</h4>
          <p className='reportName'>{report.pdfReport}</p>

          {reportFilePdf ? (
            <div className="pdfFileAndDownload">
              <a href={reportFilePdf} download={report.pdfReport}>
                <DownloadIcon className="icon" />
                {report.pdfReport}
              </a>
              <iframe title={report.pdfReport} src={reportFilePdf} width="100%" height="500px" />
            </div>
          ) : null}
        </div>
      ) : null}

      {report?.excelReportId && report?.excelReport && showReportId == report["Id"] ? (
        <div onClick={() => { fetchReport(2); }} className='pdfReport'>
          <h4>Report csv:</h4>
          <p className='reportName'>{report?.excelReport}</p>
        </div>
      ) : null}

      <div className="button-container">
        {report?.videoReportId ||
        report?.videoId ||
        report?.pdfReportId ||
        report?.excelReportId ? (
          <button onClick={handleViewDetails}>View Details</button>
        ) : null}
      </div>
    </div>
  );
};

export default ReportItem;